import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Users,
  Star,
  BarChart3,
  FileText,
  PieChart,
  TrendingUp,
  MessageSquare,
  Filter,
  Download,
  RefreshCw
} from 'lucide-react';
import { DynamicChart, ChartData } from './DynamicChart';
import { MetricCard } from '@/components/dashboard/ChartComponents';

interface AnalysisData {
  totalResponses: number;
  ratings: Record<string, Array<{rating: number; count: number}>>;
  demographics: Record<string, Record<string, number>>;
  choices: Record<string, Record<string, number>>;
  textResponses: Record<string, string[]>;
  areaAverages: Record<string, number>;
  areaDistributions: Record<string, Array<{ rating: number; count: number }>>;
}

interface DashboardLayoutProps {
  selectedForm: any;
  analysisData: AnalysisData | null;
  isLoadingSubmissions: boolean;
  onRefresh: () => void;
  calculateAverage: (ratings: Array<{rating: number; count: number}>) => number;
}

export const DashboardLayout: React.FC<DashboardLayoutProps> = ({
  selectedForm,
  analysisData,
  isLoadingSubmissions,
  onRefresh,
  calculateAverage
}) => {
  const [activeTab, setActiveTab] = useState('satisfaction');
  const [expandedQuestions, setExpandedQuestions] = useState<Set<string>>(new Set());

  const toggleQuestionExpansion = (question: string) => {
    const newExpanded = new Set(expandedQuestions);
    if (newExpanded.has(question)) {
      newExpanded.delete(question);
    } else {
      newExpanded.add(question);
    }
    setExpandedQuestions(newExpanded);
  };

  if (isLoadingSubmissions) {
    return (
      <div className="space-y-6">
        {/* Header Skeleton */}
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <Skeleton className="h-9 w-56" />
            <div className="flex items-center gap-2">
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-5 w-24" />
            </div>
          </div>
        </div>
        {/* Metrics Skeleton */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <Skeleton className="h-4 w-28" />
                <Skeleton className="h-4 w-4" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-20 mb-1" />
                <Skeleton className="h-3 w-32" />
              </CardContent>
            </Card>
          ))}
        </div>
        {/* Tabs Skeleton */}
        <Skeleton className="h-10 w-full" />
        {/* Charts Skeleton */}
        <div className="grid gap-4 md:grid-cols-2">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-5 w-48" />
                <Skeleton className="h-4 w-36" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-48 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (!analysisData) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <FileText className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-xl font-semibold mb-2">Sin datos disponibles</h3>
          <p className="text-muted-foreground">
            No se pudieron cargar los datos del formulario seleccionado.
          </p>
        </CardContent>
      </Card>
    );
  }

  // Preparar datos para charts
  const convertToChartData = (data: Record<string, number>): ChartData[] => {
    return Object.entries(data).map(([name, value]) => ({ name, value }));
  };

  // Identificar diferentes tipos de preguntas
  const questionNameMapping: Record<string, string> = {
    'experiencia': 'Experiencia general',
    'atención': 'Atención recibida',
    'tiempo que tarda': 'Tiempo de respuesta', 
    'probabilidad': 'Probabilidad recomendación',
    'recomend': 'Probabilidad recomendación',
    'valora del 1 al 10 el servicio de las áreas': 'Servicio de áreas'
  };

  // Filtrar y mapear preguntas de satisfacción (EXCLUYENDO la pregunta de áreas)
  const satisfactionQuestions = Object.entries(analysisData.ratings)
    .filter(([question]) => {
      const lowerQuestion = question.toLowerCase();
      
      // EXCLUIR la pregunta de valoración de áreas (se maneja por separado)
      if (lowerQuestion.includes('valora del 1 al 10 el servicio de las áreas') || 
          lowerQuestion.includes('servicio de las áreas') || 
          lowerQuestion.includes('áreas con la') ||
          lowerQuestion.includes('trato habitual')) {
        console.log(`🚫 EXCLUIDO de satisfacción normal: "${question}"`);
        return false; // Cambio crítico: EXCLUIR en lugar de incluir
      }
      
      // Excluir preguntas de tiempo en el rol
      return !(lowerQuestion.includes('tiempo') && lowerQuestion.includes('rol'));
    })
    .map(([question, ratings]) => {
      // Buscar mapeo de nombre basado en palabras clave
      const lowerQuestion = question.toLowerCase();
      let mappedName = question;
      
      for (const [keyword, displayName] of Object.entries(questionNameMapping)) {
        if (lowerQuestion.includes(keyword.toLowerCase())) {
          mappedName = displayName;
          break;
        }
      }
      
      return [question, ratings, mappedName] as [string, any, string];
    });
  const demographicQuestions = Object.entries(analysisData.demographics);
  const choiceQuestions = Object.entries(analysisData.choices)
    .filter(([question]) => {
      const lowerQuestion = question.toLowerCase();
      
      // Solo mantener estas preguntas específicas
      const allowedQuestions = [
        '¿considera que fue clara y comprensible la información proporcionada?',
        '¿su problema o consulta fue resuelto de manera efectiva?',
        '¿tiene claros los entregables de las solicitudes de asesoría jurídica?',
        '¿conoce el sla (acuerdo de nivel de servicios) para las solicitudes de asesoría jurídica?'
      ];
      
      // Verificar si la pregunta coincide con alguna de las permitidas
      return allowedQuestions.some(allowed => 
        lowerQuestion.includes(allowed.toLowerCase()) || 
        allowed.toLowerCase().includes(lowerQuestion)
      );
    });
  const textQuestions = Object.entries(analysisData.textResponses)
    .filter(([question]) => {
      const lowerQuestion = question.toLowerCase();
      
      // Solo mantener estas preguntas abiertas específicas
      const allowedOpenQuestions = [
        '¿cuál es el valor agregado que está aportando esta asesoría jurídica?',
        '¿en qué medida este servicio le proporciona seguridad jurídica?',
        '¿qué es lo que más valora de este servicio jurídico?',
        '¿qué le ha faltado para que este servicio jurídico cumpliera sus necesidades?',
        'comparta alguna experiencia en la que se ha sentido apoyado por este servicio',
        'comparta alguna experiencia en la que el servicio no fue satisfactorio',
        '¿en qué servicio de jurídico ha experimentado más dificultades y debería ser nuestra prioridad de mejora?'
      ];
      
      // Verificar si la pregunta coincide con alguna de las permitidas
      return allowedOpenQuestions.some(allowed => 
        lowerQuestion.includes(allowed.toLowerCase()) || 
        allowed.toLowerCase().includes(lowerQuestion.replace(/[^\w\sáéíóúñü]/g, ''))
      );
    });

  // Calcular métricas generales
  const totalResponses = analysisData.totalResponses;
  const overallSatisfaction = satisfactionQuestions.length > 0
    ? (satisfactionQuestions.reduce((sum, [_, ratings, __]) => sum + calculateAverage(ratings), 0) / satisfactionQuestions.length).toFixed(1)
    : "0";

  const completionRate = selectedForm?.submission_count 
    ? Math.round((totalResponses / selectedForm.submission_count) * 100)
    : 0;

  return (
    <div className="space-y-6">
      {/* Header del Dashboard */}
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h1 className="text-3xl font-bold tracking-tight">Dashboard Analytics</h1>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs">
              {selectedForm?.title || 'Formulario seleccionado'}
            </Badge>
            <Badge variant="secondary" className="text-xs">
              {totalResponses} respuestas
            </Badge>
          </div>
        </div>
        {/*
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={onRefresh}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Actualizar
          </Button>
          <Button variant="outline" size="sm">
            <Download className="h-4 w-4 mr-2" />
            Exportar
          </Button>
          <Button variant="outline" size="sm">
            <Filter className="h-4 w-4 mr-2" />
            Filtros
          </Button>
        </div>
        */}
      </div>

      {/* Métricas Principales */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          title="Total Respuestas"
          value={totalResponses.toString()}
          description="Respuestas recopiladas"
          icon={<Users className="h-4 w-4" />}
        />
        <MetricCard
          title="Satisfacción Promedio"
          value={`${overallSatisfaction}/10`}
          description="Calificación general"
          icon={<Star className="h-4 w-4" />}
        />
        <MetricCard
          title="Tasa de Completado"
          value={`${completionRate}%`}
          description="Formularios completados"
          icon={<TrendingUp className="h-4 w-4" />}
        />
        <MetricCard
          title="Preguntas Analizadas"
          value={(satisfactionQuestions.length + demographicQuestions.length + choiceQuestions.length).toString()}
          description="Total de preguntas"
          icon={<BarChart3 className="h-4 w-4" />}
        />
      </div>

      {/* Tabs para diferentes vistas */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="demographics" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Perfil usuario
          </TabsTrigger>
          <TabsTrigger value="satisfaction" className="flex items-center gap-2">
            <Star className="h-4 w-4" />
            Satisfacción
          </TabsTrigger>
          <TabsTrigger value="responses" className="flex items-center gap-2">
            <PieChart className="h-4 w-4" />
            Preguntas Cerradas
          </TabsTrigger>
          <TabsTrigger value="feedback" className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4" />
            Preguntas abiertas
          </TabsTrigger>
        </TabsList>


        <TabsContent value="satisfaction" className="space-y-6">
          {/* 1. Chart principal de satisfacción */}
          {satisfactionQuestions.length > 0 && (
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  Tendencia de Satisfacción General
                </CardTitle>
                <CardDescription>
                  Promedio de todas las preguntas de satisfacción
                </CardDescription>
              </CardHeader>
              <CardContent>
                <DynamicChart
                  title="Satisfacción por Pregunta"
                  data={satisfactionQuestions.map(([originalQuestion, ratings, mappedName]) => ({
                    name: mappedName.length > 40 ? mappedName.substring(0, 40) + '...' : mappedName,
                    fullName: originalQuestion,
                    value: Math.round(calculateAverage(ratings) * 10) / 10
                  }))}
                  defaultType="bar"
                />
              </CardContent>
            </Card>
          )}
          
          {/* 2. Los 4 gráficos de satisfacción individuales */}
          <div className="grid gap-4 md:grid-cols-2">
            {satisfactionQuestions.map(([originalQuestion, ratings, mappedName]) => {
              // Generar datos completos del 1 al 10, llenando con 0 las calificaciones sin respuestas
              const completeRatings = Array.from({ length: 10 }, (_, index) => {
                const rating = index + 1;
                const existingRating = ratings.find(r => r.rating === rating);
                return {
                  name: `${rating}`,
                  value: existingRating ? existingRating.count : 0
                };
              });

              const average = calculateAverage(ratings);
              
              return (
                <DynamicChart
                  key={originalQuestion}
                  title={originalQuestion}
                  description={`Distribución de calificaciones`}
                  data={completeRatings}
                  defaultType="bar"
                  isRatingChart={true}
                  averageValue={average}
                />
              );
            })}
          </div>

          {/* 3. Chart de valoración por áreas - Resumen */}
          {analysisData.areaAverages && Object.keys(analysisData.areaAverages).length > 0 && (
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Valoración Promedio por Área de Servicio
                </CardTitle>
                <CardDescription>
                  Media de calificaciones (1-10) por área funcional
                </CardDescription>
              </CardHeader>
              <CardContent>
                  <DynamicChart
                    title="Valoración por Área"
                    data={Object.entries(analysisData.areaAverages)
                      .filter(([_, average]) => average > 0)
                      .map(([area, average]) => ({
                        name: area,
                        value: average
                      }))
                      .sort((a, b) => b.value - a.value)
                    }
                    defaultType="bar"
                  />
              </CardContent>
            </Card>
          )}
          
          {/* 4. Gráficos individuales por área - DATOS REALES */}
          <div className="grid gap-4 md:grid-cols-2">
            {analysisData.areaDistributions && Object.entries(analysisData.areaDistributions).map(([area, distribution]) => {
              if (distribution.length === 0) return null;
              
              console.log(`📊 ÁREA INDIVIDUAL - ${area}:`, distribution);
              
              // Generar datos completos del 1 al 10
              const completeDistribution = Array.from({ length: 10 }, (_, index) => {
                const rating = index + 1;
                const existingRating = distribution.find(r => r.rating === rating);
                return {
                  name: `${rating}`,
                  value: existingRating ? existingRating.count : 0
                };
              });
              
              // Calcular promedio para esta área - DATOS REALES
              const totalRatings = distribution.reduce((sum, item) => sum + (item.rating * item.count), 0);
              const totalCount = distribution.reduce((sum, item) => sum + item.count, 0);
              const average = totalCount > 0 ? totalRatings / totalCount : 0;
              
              return (
                <DynamicChart
                  key={area}
                  title={area}
                  description={`Distribución de calificaciones por área`}
                  data={completeDistribution}
                  defaultType="bar"
                  isRatingChart={true}
                  averageValue={average}
                />
              );
            })}
          </div>

          {satisfactionQuestions.length === 0 && (
            <Card>
              <CardContent className="p-12 text-center">
                <Star className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">Sin datos de satisfacción</h3>
                <p className="text-muted-foreground">
                  No se encontraron preguntas de satisfacción con escala numérica.
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
        <TabsContent value="demographics" className="space-y-6">
          <div className="grid gap-4 md:grid-cols-2">
            {demographicQuestions.map(([question, data]) => {
              // Determinar el tipo de gráfico según la pregunta
              const isSex = question.toLowerCase().includes('sexo') || question.toLowerCase().includes('género') || question.toLowerCase().includes('gender');
              const isAge = question.toLowerCase().includes('edad') || question.toLowerCase().includes('años');
              const isRole = question.toLowerCase().includes('rol') || question.toLowerCase().includes('puesto') || question.toLowerCase().includes('cargo');
              
              let defaultType: "horizontalBar" | "pie" | "bar" = "horizontalBar"; // Default para otros casos
              if (isSex) {
                defaultType = "pie";
              } else if (isAge || isRole) {
                defaultType = "bar";
              }
              
              return (
                <DynamicChart
                  key={question}
                  title={question}
                  description="Distribución de respuestas"
                  data={convertToChartData(data)}
                  defaultType={defaultType}
                />
              );
            })}
          </div>
          {demographicQuestions.length === 0 && (
            <Card>
              <CardContent className="p-12 text-center">
                <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">Sin datos demográficos</h3>
                <p className="text-muted-foreground">
                  No se encontraron preguntas de selección múltiple para análisis demográfico.
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="responses" className="space-y-6">
          <div className="grid gap-4 md:grid-cols-2">
            {choiceQuestions.map(([question, choices]) => (
              <DynamicChart
                key={question}
                title={question}
                description="Distribución de opciones"
                data={convertToChartData(choices)}
                defaultType="pie"
              />
            ))}
          </div>
          {choiceQuestions.length === 0 && (
            <Card>
              <CardContent className="p-12 text-center">
                <PieChart className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">Sin datos de respuestas</h3>
                <p className="text-muted-foreground">
                  No se encontraron preguntas de opción múltiple para analizar.
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="feedback" className="space-y-6">
          <div className="grid gap-6">
            {textQuestions.map(([question, responses]) => (
              <Card key={question}>
                <CardHeader>
                  <CardTitle className="text-base">{question}</CardTitle>
                  <CardDescription>
                    {responses.length} respuesta{responses.length !== 1 ? 's' : ''}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {(expandedQuestions.has(question) ? responses : responses.slice(0, 5)).map((response: string, index: number) => (
                      <div key={index} className="p-3 bg-muted/50 rounded-lg border-l-2 border-primary/20">
                        <p className="text-sm text-muted-foreground">
                          "{response}"
                        </p>
                      </div>
                    ))}
                    {responses.length > 5 && (
                      <Button 
                        variant="outline" 
                        className="w-full"
                        onClick={() => toggleQuestionExpansion(question)}
                      >
                        {expandedQuestions.has(question) 
                          ? 'Ver menos respuestas'
                          : `Ver ${responses.length - 5} respuesta${responses.length - 5 !== 1 ? 's' : ''} más`
                        }
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          {textQuestions.length === 0 && (
            <Card>
              <CardContent className="p-12 text-center">
                <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">Sin comentarios</h3>
                <p className="text-muted-foreground">
                  No se encontraron respuestas de texto para mostrar.
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Mensaje si no hay datos */}
      {analysisData && analysisData.totalResponses === 0 && (
        <Card>
          <CardContent className="p-12 text-center">
            <FileText className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">Sin respuestas</h3>
            <p className="text-muted-foreground">
              Este formulario aún no tiene respuestas para mostrar.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};